/**
 * Location Selector Component
 * Handles the dropdown for selecting locations.
 * Uses IP geolocation to recommend and reorder the closest location when available.
 */

(function() {
  'use strict';

  const CONFIG = {
    toggleSelector: '[data-location-toggle]',
    dropdownSelector: '#locationDropdown',
    linkSelector: '[data-location]',
    activeClass: 'location-dropdown__item--active',
    recommendedClass: 'location-dropdown__item--recommended',
    geoApiUrl: 'https://ipapi.co/json/',
    /** HTTPS + CORS; used when ipapi is rate-limited or errors */
    geoFallbackUrl: 'https://get.geojs.io/v1/ip/geo.json',
    geoRetryDelayMs: 700,
    geoTimeoutMs: 5000,
    storageKey: 'kraft_location_detected_v2'
  };

  /** Must match `location_list` emojis in sections/header-original.liquid */
  var ICON_BY_HANDLE = {
    virtual: '💻',
    'new-york': '🗽',
    'los-angeles': '🌴',
    irvine: '☀️',
    'san-francisco': '🌉',
    'san-jose': '🏙️',
    austin: '🤠',
    hawaii: '🌈',
    global: '🌍'
  };

  /*
   * City lists are ARRAYS and matched exactly.
   *
   * They used to be comma-joined strings tested with String.indexOf(), which is
   * a substring search, not list membership: 'ana' matched inside 'santa ana',
   * 'orange' inside 'garden grove,orange', and so on.
   *
   * Every list below is California-only and is now gated on the region being
   * California - see isCalifornia in mapGeoToHandle. Without that gate, Newark
   * and Union City (both real Bay Area cities, and both also real NEW JERSEY
   * cities) sent NY-metro visitors to San Jose.
   */

  /** LA metro area cities (geo APIs often return the suburb, not "Los Angeles") */
  var LA_CITIES = ['los angeles','long beach','anaheim','santa ana','glendale','pasadena','torrance','pomona','fullerton','inglewood','el monte','downey','costa mesa','huntington beach','santa clarita','el cajon','simi valley','van nuys','norwalk','burbank','compton','south gate','carson','santa monica','whittier','alhambra','west covina','newport beach','mission viejo','buena park','cypress','la habra','lake forest','westminster','garden grove','orange','brea','placentia','yorba linda'];
  /** Bay Area cities that map to San Francisco */
  var SF_CITIES = ['san francisco','oakland','berkeley','daly city','south san francisco','alameda','san leandro','richmond'];
  /** South Bay / Silicon Valley -> San Jose */
  var SAN_JOSE_CITIES = ['san jose','sunnyvale','santa clara','mountain view','palo alto','cupertino','milpitas','fremont','newark','union city','los altos','saratoga','campbell','gilroy'];
  /** States the New York studio serves - the NY metro spans three of them. */
  var NY_METRO_STATES = ['NY','NJ','CT'];
  /** Austin metro (geo APIs often return the suburb rather than "Austin") */
  var AUSTIN_CITIES = ['austin','round rock','cedar park','georgetown','pflugerville','leander','san marcos','kyle','buda','lakeway','bee cave','manor','hutto','dripping springs','del valle','west lake hills'];

  function inList(list, value) {
    if (!value) return false;
    for (var i = 0; i < list.length; i++) { if (list[i] === value) return true; }
    return false;
  }

  /**
   * Rough bounding boxes to distinguish SF proper vs South Bay (San Jose) when API city is wrong.
   * SF: ~37.70–37.83 lat, -122.52 to -122.35 lon
   * San Jose / South Bay: ~37.22–37.45 lat, -122.08 to -121.75 lon
   */
  function bayAreaFromCoords(lat, lon) {
    if (lat == null || lon == null || typeof lat !== 'number' || typeof lon !== 'number') return null;
    if (isNaN(lat) || isNaN(lon)) return null;
    if (lat >= 37.65 && lon <= -122.35 && lon >= -122.55) return 'san-francisco';
    if (lat >= 37.2 && lat <= 37.5 && lon >= -122.15 && lon <= -121.7) return 'san-jose';
    if (lat >= 37.5 && lat <= 37.7 && lon <= -122.2) return 'san-francisco';
    return null;
  }

  /**
   * Normalize API payload to { city, region, regionCode, latitude, longitude } or null.
   */
  function normalizeGeoPayload(data) {
    if (!data || typeof data !== 'object') return null;
    if (data.error === true) return null;

    var city = data.city != null ? String(data.city).trim() : '';
    var region = data.region != null ? String(data.region).trim() : '';
    var regionCode = data.region_code != null ? String(data.region_code).trim()
      : (data.regionCode != null ? String(data.regionCode).trim() : '');

    var latRaw = data.latitude != null ? data.latitude : data.lat;
    var lonRaw = data.longitude != null ? data.longitude : data.lon;
    var latitude = latRaw != null && latRaw !== '' ? Number(latRaw) : null;
    var longitude = lonRaw != null && lonRaw !== '' ? Number(lonRaw) : null;
    if (latitude != null && isNaN(latitude)) latitude = null;
    if (longitude != null && isNaN(longitude)) longitude = null;

    if (!city && !region && !regionCode && latitude == null && longitude == null) return null;
    return {
      city: city || null,
      region: region || null,
      regionCode: regionCode || null,
      latitude: latitude,
      longitude: longitude
    };
  }

  /**
   * Map IP geo response (city, region, regionCode, optional lat/lon) to our location handle.
   * Returns null if no match.
   */
  function mapGeoToHandle(city, region, regionCode, lat, lon) {
    if (!city && !region && !regionCode && lat == null && lon == null) return null;
    var c = (city || '').toLowerCase().trim();
    var r = (region || '').toLowerCase().trim();
    var rc = (regionCode || '').toUpperCase().trim();
    if (r.length === 2 && rc === '' && /^[a-z]{2}$/.test(r)) {
      rc = r.toUpperCase();
      r = '';
    }

    if (c.length < 2 && r.length < 2 && !rc && lat == null && lon == null) return null;

    if (rc === 'HI' || r === 'hawaii' || c.indexOf('honolulu') !== -1) return 'hawaii';

    // NY metro first, and by state. Newark and Union City are the reason: both
    // are Bay Area cities AND New Jersey cities, so a NJ visitor has to be
    // claimed here before the California lists get a chance at them.
    if (
      inList(NY_METRO_STATES, rc) ||
      r === 'new york' || r === 'new jersey' || r === 'connecticut' ||
      c.indexOf('new york') !== -1 ||
      c === 'brooklyn' ||
      c === 'manhattan' ||
      c === 'queens' ||
      c === 'bronx' ||
      c === 'staten island'
    ) {
      return 'new-york';
    }

    // Austin is the only Texas studio, so Texas maps to it - same shape as
    // Hawaii claiming HI. Without this a visitor in Austin got no
    // recommendation at all, despite a studio in their city.
    if (rc === 'TX' || r === 'texas' || inList(AUSTIN_CITIES, c)) return 'austin';

    // Everything below is a California city list, so require California.
    var isCalifornia = (rc === 'CA' || r === 'california');
    if (!isCalifornia) return null;

    if (c === 'irvine') return 'irvine';
    if (inList(LA_CITIES, c)) return 'los-angeles';
    if (r.indexOf('orange') !== -1 && c !== 'irvine') return 'los-angeles';
    if (c.indexOf('los angeles') !== -1 || r.indexOf('los angeles') !== -1) return 'los-angeles';

    var fromCoords = bayAreaFromCoords(lat, lon);
    if (fromCoords) return fromCoords;

    if (
      c.indexOf('san francisco') !== -1 ||
      inList(SF_CITIES, c) ||
      (r.indexOf('san francisco') !== -1 && !inList(SAN_JOSE_CITIES, c))
    ) {
      return 'san-francisco';
    }
    if (inList(SAN_JOSE_CITIES, c) || c.indexOf('san jose') !== -1) return 'san-jose';

    return null;
  }

  /**
   * Single fetch to one geo URL; returns normalized geo or null.
   */
  function fetchGeoOnce(url) {
    var controller = new AbortController();
    var timeout = setTimeout(function() { controller.abort(); }, CONFIG.geoTimeoutMs);
    return fetch(url, { signal: controller.signal })
      .then(function(res) {
        return res.json()
          .then(function(data) {
            clearTimeout(timeout);
            if (!res.ok) return null;
            return normalizeGeoPayload(data);
          })
          .catch(function() {
            clearTimeout(timeout);
            return null;
          });
      })
      .catch(function() {
        clearTimeout(timeout);
        return null;
      });
  }

  /**
   * Fetch approximate location via IP (no user permission).
   * Tries ipapi.co, optional delayed retry on failure, then geojs.io fallback.
   */
  function fetchGeo() {
    return fetchGeoOnce(CONFIG.geoApiUrl).then(function(primary) {
      if (primary) return primary;
      return new Promise(function(resolve) {
        setTimeout(function() {
          fetchGeoOnce(CONFIG.geoApiUrl).then(function(retry) {
            if (retry) {
              resolve(retry);
              return;
            }
            fetchGeoOnce(CONFIG.geoFallbackUrl).then(resolve);
          });
        }, CONFIG.geoRetryDelayMs);
      });
    });
  }

  /**
   * True if dropdown has a row for this handle (excludes global hub).
   */
  function rowExistsForHandle(dropdown, handle) {
    if (!dropdown || !handle || handle === 'global') return false;
    var safe = String(handle).replace(/"/g, '');
    return !!dropdown.querySelector('[data-location="' + safe + '"]');
  }

  /**
   * Reorder dropdown: move the item with the given handle to the top and add "Closest location" label.
   * @returns {boolean} true if a row was updated
   */
  function applyRecommendedLocation(dropdown, handle) {
    var list = dropdown.querySelector('.location-dropdown__list');
    if (!list) return false;

    var items = list.querySelectorAll('.location-dropdown__item');
    var targetItem = null;
    for (var i = 0; i < items.length; i++) {
      var rowLink = items[i].querySelector(CONFIG.linkSelector);
      if (rowLink && rowLink.getAttribute('data-location') === handle) {
        targetItem = items[i];
        break;
      }
    }
    if (!targetItem) return false;

    var link = targetItem.querySelector(CONFIG.linkSelector);
    if (!link) return false;

    var existingLabels = link.querySelectorAll('.location-dropdown__recommended-label');
    for (var j = 0; j < existingLabels.length; j++) {
      existingLabels[j].parentNode.removeChild(existingLabels[j]);
    }

    targetItem.classList.add(CONFIG.recommendedClass);
    var nameEl = link.querySelector('.location-dropdown__name');
    var emoji = ICON_BY_HANDLE[handle];
    if (emoji) {
      var iconEl = link.querySelector('.location-dropdown__icon');
      if (!iconEl) {
        iconEl = document.createElement('span');
        iconEl.className = 'location-dropdown__icon';
        iconEl.setAttribute('aria-hidden', 'true');
        iconEl.textContent = emoji;
        if (nameEl) {
          link.insertBefore(iconEl, nameEl);
        } else {
          link.insertBefore(iconEl, link.firstChild);
        }
      } else {
        iconEl.textContent = emoji;
      }
    }
    var label = document.createElement('span');
    label.className = 'location-dropdown__recommended-label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = 'Closest location';
    if (nameEl && nameEl.nextSibling) {
      link.insertBefore(label, nameEl.nextSibling);
    } else {
      link.appendChild(label);
    }

    list.insertBefore(targetItem, list.firstChild);
    return true;
  }

  /**
   * Apply recommendation only if handle exists in DOM; persist to sessionStorage on success.
   * Clears storage when handle is invalid for this dropdown.
   * @returns {boolean}
   */
  function commitRecommendation(dropdown, handle) {
    if (!handle) {
      try {
        sessionStorage.removeItem(CONFIG.storageKey);
      } catch (e) {}
      return false;
    }
    if (!rowExistsForHandle(dropdown, handle)) {
      try {
        sessionStorage.removeItem(CONFIG.storageKey);
      } catch (e) {}
      return false;
    }
    if (!applyRecommendedLocation(dropdown, handle)) {
      try {
        sessionStorage.removeItem(CONFIG.storageKey);
      } catch (e) {}
      return false;
    }
    try {
      sessionStorage.setItem(CONFIG.storageKey, handle);
    } catch (e) {}
    return true;
  }

  /**
   * Run geo detection, optionally use cache, then reorder and mark recommended.
   */
  function runLocationDetection(dropdown) {
    if (!dropdown) return;

    var cached = null;
    try {
      cached = sessionStorage.getItem(CONFIG.storageKey);
    } catch (e) {}

    if (cached && commitRecommendation(dropdown, cached)) {
      return;
    }

    fetchGeo().then(function(geo) {
      if (!geo) return;
      var handle = mapGeoToHandle(
        geo.city,
        geo.region,
        geo.regionCode,
        geo.latitude,
        geo.longitude
      );
      commitRecommendation(dropdown, handle);
    });
  }

  // Initialize location selector
  function init() {
    const toggle = document.querySelector(CONFIG.toggleSelector);
    const dropdown = document.querySelector(CONFIG.dropdownSelector);

    if (!toggle || !dropdown) return;

    // Toggle dropdown
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleDropdown(toggle, dropdown);
    });

    // Handle location selection
    const links = dropdown.querySelectorAll(CONFIG.linkSelector);
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        // Don't prevent default - let the link navigate
        handleLocationSelect(link, toggle, dropdown);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!toggle.contains(e.target) && !dropdown.contains(e.target)) {
        closeDropdown(toggle, dropdown);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && dropdown.getAttribute('aria-hidden') === 'false') {
        closeDropdown(toggle, dropdown);
        toggle.focus();
      }
    });

    // Location detection: reorder and recommend closest (async, non-blocking).
    //
    // Only on the non-localised header. applyRecommendedLocation() ends with
    // list.insertBefore(targetItem, list.firstChild) and adds a "Closest
    // location" label, so on a city page it lifted the IP-geo match above the
    // city the visitor is actually looking at - a New York page recommending
    // San Jose. Someone on /pages/team-building-nyc has already chosen; the
    // recommendation is for people who have not.
    //
    // data-current-location is rendered by header-original.liquid from
    // page_location, and is the empty string on global pages.
    var currentLocation = (toggle.getAttribute('data-current-location') || '').trim();
    if (!currentLocation) {
      runLocationDetection(dropdown);
    }
  }

  // Toggle dropdown open/close
  function toggleDropdown(toggle, dropdown) {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      closeDropdown(toggle, dropdown);
    } else {
      openDropdown(toggle, dropdown);
    }
  }

  // Open dropdown
  function openDropdown(toggle, dropdown) {
    toggle.setAttribute('aria-expanded', 'true');
    dropdown.setAttribute('aria-hidden', 'false');
  }

  // Close dropdown
  function closeDropdown(toggle, dropdown) {
    toggle.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('aria-hidden', 'true');
  }

  // Handle location selection
  function handleLocationSelect(link, toggle, dropdown) {
    const location = link.getAttribute('data-location');
    const nameNode = link.querySelector('.location-dropdown__name');
    const locationName = nameNode ? nameNode.textContent : '';

    // Close dropdown (page will navigate via the link href)
    closeDropdown(toggle, dropdown);

    // Optional: Trigger custom event for analytics or other tracking
    const event = new CustomEvent('locationChange', {
      detail: { location, locationName }
    });
    document.dispatchEvent(event);

    // Note: No need to update UI - the link will navigate to the new page
    // and the correct active state will be shown on page load
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
